import React, { useState, useEffect, useCallback } from 'react';
import { 
  CContainer, CRow, CCol, CCard, CCardBody, CCardHeader, 
  CAvatar, CBadge, CButton, CSpinner, CFormInput, CFormLabel, 
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter
} from '@coreui/react';
import { CreditCard, LogOut, Clock, Edit3, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosInstance';
 

// Professional Accent Color Map for Tiers
const TIER_CONFIG = {
  Alpha: { color: '#FF5722', accentBg: 'rgba(255, 87, 34, 0.08)' },
  Beeta: { color: '#10B981', accentBg: 'rgba(16, 185, 129, 0.08)' },
  Gamma: { color: '#3B82F6', accentBg: 'rgba(59, 130, 246, 0.08)' },
  Delta: { color: '#A855F7', accentBg: 'rgba(168, 85, 247, 0.08)' },
  Sigma: { color: '#F59E0B', accentBg: 'rgba(245, 158, 11, 0.08)' },
};

const UserProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // Profile Edit Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ fullName: '', email: '' });
  const [saving, setSaving] = useState(false);

  // Upgrade Plan Modal States
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [upgrading, setUpgrading] = useState(false);

  const memberId = localStorage.getItem('ID') || 1;

  // Fetch Profile & Subscription details
  const fetchProfileAndSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const [profileRes, subRes] = await Promise.allSettled([
        api.get(`/api/members/profile/${memberId}`),
        api.get(`/api/members/${memberId}/subscription`)
      ]);

      if (profileRes.status === 'fulfilled') {
        setProfile(profileRes.value.data);
        setEditForm({
          fullName: profileRes.value.data.fullName || '',
          email: profileRes.value.data.email || ''
        });
      }

      if (subRes.status === 'fulfilled') {
        setSubscription(subRes.value.data);
        
      }
    } catch (err) {
      console.error("Error loading member data:", err);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchProfileAndSubscription();
  }, [fetchProfileAndSubscription]);

  // Fetch All Tiers for Modal
  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const res = await api.get('/api/membershiptiers/GetAllTiers');
        setTiers(res.data);
         
      } catch (err) {
        console.error("Failed to load tiers", err);
      }
    };
    fetchTiers();
  }, []);

  // Upgrade Plan Submit Handler
  const handleUpgradeTier = async (selectedTier) => {
    try {
      setUpgrading(true);
      await api.post(`/api/members/${memberId}/upgrade`, {
        tierId: selectedTier.tierId,
        amount: selectedTier.price
      });
       // 2. Subscription & Validity Check logic
             
                const subRes = await api.get(`/api/members/${memberId}/subscription`);
                const subData = subRes.data;
      
                if (subData) {
                  let isPlanValid = true;
      
                  // Check Expiry Date (Agar Expiry date null nahi hai aur current date se past ki hai)
                  if (subData.endDate) {
                    const expiryTime = new Date(subData.endDate).getTime();
                    const currentTime = new Date().getTime();
      
                    if (currentTime > expiryTime) {
                      isPlanValid = false;
                    }
                  }
      
                  // Save Subscription Details in localStorage
                  localStorage.setItem('subscriptionTier', subData.tierName || 'Alpha');
                  localStorage.setItem('isSubscriptionActive', isPlanValid ? 'true' : 'false');
                  localStorage.setItem('expiryDate', subData.endDate || 'Lifetime');
      
                  // Expiry Alert Notification (Agar plan expire ho gaya ho)
                //   if (!isPlanValid) {
                //     setToast(
                //       <CToast color="warning" className="text-dark">
                //         <CToastBody>Aapka subscription plan expire ho chuka hai. Kripya ise renew karein.</CToastBody>
                //       </CToast>
                //     );
                //   }
                }
              
      setShowUpgradeModal(false);
      fetchProfileAndSubscription();
    } catch (err) {
      console.error("Upgrade failed", err);
    } finally {
      setUpgrading(false);
    }
  };

  // Profile Update Handler
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.put(`/api/members/profile/${memberId}`, editForm);
      setShowEditModal(false);
      fetchProfileAndSubscription();
    } catch (err) {
      console.error("Failed to update profile", err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('memberId');
    navigate('/login');
  };

  if (loading) {
    return (
      <CContainer className="text-center py-5 my-5">
        <CSpinner color="primary" size="lg" />
        <p className="mt-3 fw-bold text-dark">Profile loading...</p>
      </CContainer>
    );
  }

  return (
    <div className="bg-light py-5 min-vh-100">
      <CContainer style={{ maxWidth: '1000px' }}>
        <CRow className="g-4">
          
          {/* Left Side: Profile Summary Card */}
          <CCol md={4}>
            <CCard className="border-0 shadow-sm rounded-4 text-center p-4">
              <CCardBody className="d-flex flex-column align-items-center">
                <CAvatar 
                  color="primary" 
                  textColor="white" 
                  size="xl" 
                  className="fw-bold fs-2 shadow-sm mb-3"
                  style={{ width: '80px', height: '80px' }}
                >
                  {profile?.fullName ? profile.fullName.charAt(0).toUpperCase() : 'U'}
                </CAvatar>

                <h5 className="fw-bold mb-1 text-dark">{profile?.fullName || 'N/A'}</h5>
                <p className="text-muted small mb-3">{profile?.email || 'N/A'}</p>

                <div className="d-flex gap-2 mb-3">
                  <CBadge color={profile?.isActive ? 'success' : 'danger'} className="px-3 py-2 rounded-pill">
                    {profile?.isActive ? 'Active Account' : 'Inactive Account'}
                  </CBadge>
                </div>

                <CButton 
                  color="light" 
                  size="sm" 
                  className="w-100 fw-bold border mb-3 d-flex align-items-center justify-content-center gap-1"
                  onClick={() => setShowEditModal(true)}
                >
                  <Edit3 size={14} /> Edit Profile
                </CButton>

                <div className="w-100 border-top pt-3 text-start">
                  <small className="text-muted d-block mb-1">Total Spent:</small>
                  <h6 className="fw-bold text-success mb-3">₹{profile?.totalPaidAmount || 0}</h6>
                  
                  <CButton 
                    color="danger" 
                    variant="outline" 
                    className="w-100 fw-bold d-flex align-items-center justify-content-center gap-2"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> Logout
                  </CButton>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          {/* Right Side: Detailed Subscription & Payments */}
          <CCol md={8}>
            
            {/* Active Plan Card with Upgrade Button */}
            <CCard className="border-0 shadow-sm rounded-4 mb-4">
              <CCardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold text-dark m-0 d-flex align-items-center gap-2">
                  <CreditCard size={18} className="text-primary" /> Active Plan Details
                </h6>
                <CButton 
                  color="primary" 
                  size="sm" 
                  className="fw-bold d-flex align-items-center gap-1 cursor-pointer"
                  onClick={() => setShowUpgradeModal(true)}
                >
                  <ArrowUpRight size={16} /> Upgrade Plan
                </CButton>
              </CCardHeader>
              <CCardBody className="p-4">
                {subscription ? (
                  <CRow className="g-3 align-items-center">
                    <CCol sm={6}>
                      <span className="text-muted small">Current Tier</span>
                      <h5 className="fw-bold text-dark mb-0">{subscription.tierName}</h5>
                    </CCol>

                    <CCol sm={6} className="text-sm-end">
                      {subscription.tierName === "Alpha" || subscription.price === 0 ? (
                        <CBadge color="success" className="p-2">Free</CBadge>
                      ) : (
                        <CBadge color="primary" className="p-2">
                          ₹{subscription.price }
                        </CBadge>
                      )}
                    </CCol>

                    <CCol sm={6}>
                      <small className="text-muted d-block">Start Date</small>
                      <span className="fw-medium">
                        {subscription.startDate ? new Date(subscription.startDate).toLocaleDateString() : 'N/A'}
                      </span>
                    </CCol>

                    <CCol sm={6}>
                      <small className="text-muted d-block">End Date / Duration</small>
                      <span className="fw-medium">
                        {subscription.endDate ? (
                          new Date(subscription.endDate).toLocaleDateString()
                        ) : subscription.durationInDays !== null && subscription.durationInDays > 0 ? (
                          `${subscription.durationInDays} Days`
                        ) : (
                          "Lifetime / Unlimited"
                        )}
                      </span>
                    </CCol>
                  </CRow>
                ) : (
                  <div className="text-center py-3">
                    <p className="text-muted mb-2">No active subscription plan found.</p>
                    <CButton color="primary" size="sm" onClick={() => setShowUpgradeModal(true)}>
                      Upgrade Plan
                    </CButton>
                  </div>
                )}
              </CCardBody>
            </CCard>

            {/* Payment History Card */}
            <CCard className="border-0 shadow-sm rounded-4">
              <CCardHeader className="bg-white border-bottom py-3">
                <h6 className="fw-bold text-dark m-0 d-flex align-items-center gap-2">
                  <Clock size={18} className="text-info" /> Recent Transactions
                </h6>
              </CCardHeader>
              <CCardBody className="p-0">
                {profile?.recentPayments && profile.recentPayments.length > 0 ? (
                  <CTable align="middle" responsive hover className="mb-0">
                    <CTableHead color="light">
                      <CTableRow>
                        <CTableHeaderCell>Date</CTableHeaderCell>
                        <CTableHeaderCell>Amount</CTableHeaderCell>
                        <CTableHeaderCell>Status</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {profile.recentPayments.map((pay, index) => (
                        <CTableRow key={index}>
                          <CTableDataCell>{new Date(pay.paymentDate).toLocaleDateString()}</CTableDataCell>
                          <CTableDataCell className="fw-bold">₹{pay.amount}</CTableDataCell>
                          <CTableDataCell>
                            <CBadge color={pay.status === 'Success' ? 'success' : 'danger'}>
                              {pay.status}
                            </CBadge>
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                ) : (
                  <p className="text-muted text-center py-4 mb-0">No payment records found.</p>
                )}
              </CCardBody>
            </CCard>

          </CCol>
        </CRow>
      </CContainer>

      {/* Edit Profile Modal */}
      <CModal visible={showEditModal} onClose={() => setShowEditModal(false)}>
        <CModalHeader onClose={() => setShowEditModal(false)}>
          <CModalTitle className="fw-bold h5">Edit Profile</CModalTitle>
        </CModalHeader>
        <form onSubmit={handleUpdateProfile}>
          <CModalBody>
            <div className="mb-3">
              <CFormLabel className="fw-semibold">Full Name</CFormLabel>
              <CFormInput 
                type="text" 
                value={editForm.fullName} 
                onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                required 
              />
            </div>
            <div className="mb-3">
              <CFormLabel className="fw-semibold">Email Address</CFormLabel>
              <CFormInput 
                type="email" 
                value={editForm.email} 
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required 
                readOnly
              />
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={() => setShowEditModal(false)}>Cancel</CButton>
            <CButton color="primary" type="submit" disabled={saving}>
              {saving ? <CSpinner size="sm" /> : 'Save Changes'}
            </CButton>
          </CModalFooter>
        </form>
      </CModal>

      {/* Upgrade Plan Modal */}
      <CModal
        size="lg"
        visible={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        centered
      >
        <CModalHeader onClose={() => setShowUpgradeModal(false)} className="border-bottom-0 pb-0 pt-3 px-4">
          <div>
            <CModalTitle className="fw-bold fs-5 text-dark mb-1">Select a Plan</CModalTitle>
            <p className="text-muted small mb-0">Choose the best plan suited for your learning needs.</p>
          </div>
        </CModalHeader>

        <CModalBody className="p-4">
          <CRow className="g-3">
            {tiers.map((t) => {
              const config = TIER_CONFIG[t.tierName] || { color: '#6B7280', accentBg: '#F3F4F6' };
              const isCurrent = subscription?.tierName === t.tierName;

              return (
                <CCol md={6} key={t.tierId}>
                  <div
                    className="p-3 rounded-3 position-relative"
                    style={{
                      backgroundColor: isCurrent ? config.accentBg : '#FFFFFF',
                      border: `1.5px solid ${isCurrent ? config.color : '#E5E7EB'}`,
                      borderLeft: `5px solid ${config.color}`,
                      boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span className="fw-bold text-dark fs-6">{t.tierName}</span>
                          {isCurrent && (
                            <CBadge
                              className="px-2 py-1 fw-medium"
                              style={{
                                backgroundColor: config.color,
                                color: '#FFF',
                                fontSize: '10px',
                                borderRadius: '4px',
                              }}
                            >
                              CURRENT PLAN
                            </CBadge>
                          )}
                        </div>

                        <div className="d-flex align-items-baseline gap-1">
                          <span className="fw-bold text-dark fs-4">
                            {t.price === 0 ? 'Free' : `₹${t.price}`}
                          </span>
                          <span className="text-muted small">
                            / {t.durationInDays ? `${t.durationInDays} days` : 'Lifetime'}
                          </span>
                        </div>
                      </div>

                      <CButton
                        size="sm"
                        className="px-3 py-1.5 fw-semibold border-0 rounded-2"
                        style={{
                          backgroundColor: isCurrent ? '#9CA3AF' : config.color,
                          color: '#FFFFFF',
                          fontSize: '13px',
                          cursor: isCurrent ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isCurrent || upgrading}
                        onClick={() => handleUpgradeTier(t)}
                      >
                        {isCurrent ? 'Active' : 'Upgrade'}
                      </CButton>
                    </div>
                  </div>
                </CCol>
              );
            })}
          </CRow>
        </CModalBody>
      </CModal>

    </div>
  );
};

export default UserProfile;